// @flow
import { get } from 'lodash';
import moment from 'moment';

import * as trackingApi from 'Tracking/tracking-api';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { BE_TRACKING } from 'Tracking/constants';
import {
  proposalsByIdQuery,
  type ProposalsByIdQueryParam,
} from 'Views/booking/BookingEngine.query';
import {
  PAGE_BE_ACCOMMODATION,
  getBookingEngineUri,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import redirectUtils from 'Utils/uri/redirectUtils';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { hasFetchProposalsFailed } from 'Utils/proposal/proposalUtils';
import {
  addAccommodationMutation,
  addTransportMutation,
  fetchPayment,
  proposalsQuery,
  promoCodeProposalMutation,
  putServicesMutation,
  deleteServicesMutation,
  type ServicesMutationType,
  switchProposalsOffers as switchProposalsOffersMutation,
  addServiceMutation,
  type OverloadForSpecificProposalIdsType,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  getSigninURIWithRedirection,
  isSeller,
  saveCustomerProposal,
} from 'Utils/seller/sellerUtils';
import {
  updateAttendeesMutation,
  type updateAttendeesMutationType,
} from 'Utils/graphql-query-helper/booking/attendees/attendeesQuery';
import {
  updateInsurancesMutation,
  deleteInsurancesMutation,
} from 'Utils/graphql-query-helper/booking/insurances/insurancesQuery';
import { type BookingEngineEntry, BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import { proposalsSelector, getCurrentProposal } from 'Store/booking/booking.selector';
import { getArrivalDate } from 'Store/trip-date-criteria/storeHelper';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { getKids, getAdultsNumber, getAdults } from 'Store/trip-information/storeHelper';
import createAction, { createOptimisticAction } from 'Store/utils/createAction';
import logger from 'Logger/logger';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { getBookingEngineI18n } from 'Store/cms/cms.actions';

import {
  ADD_ACCOMMODATION,
  ADD_TRANSPORT,
  ADD_SERVICE,
  CLEAR_PROPOSALS,
  DELETE_SERVICE,
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  FETCH_SELECTED_PROPOSAL_PAYMENT,
  SAVE_CUSTOMER_PROPOSAL,
  UPDATE_INSURANCES,
  DELETE_INSURANCES,
  PREPARE_BE,
  DISPLAY_TOAST_ON_CONTEXT_SHARE,
  FETCH_PROPOSALS_FROM_EDIT_CRITERIA,
  SAVE_DONATION_TO_PROPOSALS,
  SET_PROMO_CODE,
  UPDATE_ATTENDEES,
  UPDATE_SERVICES,
  SWITCH_PROPOSALS_OFFERS,
} from './constants';

export type FetchProposalsParams = {|
  departureCity: string,
  duration: number,
  isJoyView?: boolean,
  productId: string,
  globalLoaderLabel?: string,
  adultsAttendeesIds?: Array<string>,
  kidsAttendeesIds?: Array<string>,
  newArrivalDate?: string,
  bookingEngineEntry: BookingEngineEntry,
|};

export type AdditionalTrackingParams = {
  departureCityName?: string,
  shouldTrackTransport?: boolean,
};

const BOOKING_ENGINE_ENTRY_TRACKING_LABELS = {
  [BOOKING_ENGINE_ENTRIES.PRICE_CALENDAR]: 'calendarPrice',
  [BOOKING_ENGINE_ENTRIES.PRICE_REMOTE]: 'remotePrice',
  [BOOKING_ENGINE_ENTRIES.QUICK_SEARCH]: 'quickSearch',
  [BOOKING_ENGINE_ENTRIES.CRUISE_BOOKING]: 'cruiseBooking',
};

function setPromoCode(
  proposalId: string,
  params: {| promoCode: string, partnerId?: string, accessCode?: string, customerId: number |},
  appContext: AppContext,
) {
  return createOptimisticAction(
    SET_PROMO_CODE,
    {},
    {
      optimist: true,
      promise: promoCodeProposalMutation({ proposalId, ...params }, appContext),
    },
  );
}

function fetchProposalsById({
  proposalsId,
  appContext,
  shouldRequestPayment,
  adultsAttendeesIds,
  kidsAttendeesIds,
}: ProposalsByIdQueryParam & { appContext: AppContext }) {
  return (dispatch: ReduxDispatch) => {
    return dispatch(
      createOptimisticAction(
        FETCH_PROPOSALS_BY_ID,
        {},
        {
          optimist: true,
          promise: proposalsByIdQuery(
            {
              proposalsId,
              shouldRequestPayment,
              adultsAttendeesIds,
              kidsAttendeesIds,
              fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
            },
            appContext,
          ),
        },
      ),
    );
  };
}

/*
  ATTENTION:
  Les valeurs par défaut de departureCity et duration sont mises dans le CMS,
  on ne peut donc pas récupérer les valeurs du store
 */
function fetchProposalsAndRedirect(
  params: FetchProposalsParams,
  additionalTrackingParams: AdditionalTrackingParams,
  appContext: AppContext,
  location: ProductRouterLocation,
) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const kids = getKids(state) || [];
    const adults = getAdults(state) || [];
    const nbAdults = getAdultsNumber(state) || DEFAULT_ADULTS_NB;
    const numberAttendees = nbAdults + kids.length;
    const intArrivalDate = params.newArrivalDate
      ? params.newArrivalDate
      : getArrivalDate(state) || timestampToApiDate(state.tripCriteria.departureDate);
    const redirectUri = getSigninURIWithRedirection({
      locale: appContext.locale,
      proposalId: null,
      step: null,
      isCmta: appContext.isCmta,
    });
    if (!intArrivalDate) return;

    const arrivalDate = intArrivalDate.toString();
    const proposalParams = {
      arrivalDate,
      kids,
      adults,
      numberAttendees,
      fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
      ...(isSeller(appContext)
        ? { salesNetworkId: location.queryParams.cxo_sales_network_id }
        : null),
      ...params,
    };

    const payload = {
      globalLoaderLabel: params.globalLoaderLabel,
      bookingEngineEntry: params.bookingEngineEntry,
    };
    const promise = Promise.all([
      proposalsQuery(proposalParams, appContext),
      dispatch(getBookingEngineI18n(appContext)),
    ]).then(([response]) => {
      const { proposals } = response;
      const isFetchProposalsFailed = hasFetchProposalsFailed(proposals);

      if (!isFetchProposalsFailed) {
        setTimeout(() => {
          const bookingUrl = getBookingEngineUri(
            PAGE_BE_ACCOMMODATION,
            proposals.proposals || [],
            proposals.resort,
            undefined,
            location.queryParams,
          );

          // $FlowFixMe
          redirectUtils.redirectTo()(bookingUrl);
        }, 0);
      }

      trackingApi.setDataLayer(
        {
          categoryGA: 'resort',
          actionGA: BOOKING_ENGINE_ENTRY_TRACKING_LABELS[params.bookingEngineEntry],
          labelGA: isFetchProposalsFailed ? 'errorCheckPrices' : 'checkPrices',
        },
        appContext,
      );

      return response;
    });
    return dispatch(
      createOptimisticAction(FETCH_PROPOSALS, payload, {
        optimist: true,
        promise,
        redirect_uri: redirectUri,
      }),
    );
  };
}

function editCriteriaFetchProposals(
  criteria: *,
  proposalsLoderLabel: string,
  appContext: AppContext,
  location: BookingRouterLocation,
) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const { search, departure, attendees, departureCity } = criteria;
    const payload = { globalLoaderLabel: proposalsLoderLabel };
    const state = getState();

    return dispatch(
      createOptimisticAction(FETCH_PROPOSALS_FROM_EDIT_CRITERIA, payload, {
        optimist: true,
        promise: proposalsQuery(
          {
            productId: search.value,
            arrivalDate: moment(departure.date).format('YYYYMMDD'),
            duration: departure.duration,
            departureCity: departureCity.value,
            numberAttendees: attendees.adults + attendees.kids.length,
            adults: new Array(attendees.adults).fill({}),
            // $FlowFixMe
            kids: attendees.kids,
            isJoyView: criteria.isJoyView,
            fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
            adultsAttendeesIds: state.tripInformation.adults
              .map(({ neolid }) => neolid)
              .filter(Boolean),
            kidsAttendeesIds: state.tripInformation.kids
              .map(({ neolid }) => neolid)
              .filter(Boolean),
            ...(isSeller(appContext)
              ? { salesNetworkId: location.queryParams.cxo_sales_network_id }
              : null),
          },
          appContext,
        ),
      }),
    );
  };
}

function simpleFetchProposals(
  criterias: {
    productId: string,
    arrivalDate: string,
    duration: number,
    attendees: { adults: number, kids: Array<*> },
    departureCity: string,
    bookingEngineEntry: BookingEngineEntry,
  },
  proposalsLoaderLabel: string,
  appContext: AppContext,
  location: BookingRouterLocation,
) {
  const {
    productId,
    arrivalDate,
    duration,
    attendees,
    departureCity,
    bookingEngineEntry,
  } = criterias;

  const payload = { globalLoaderLabel: proposalsLoaderLabel, bookingEngineEntry };

  return createOptimisticAction(FETCH_PROPOSALS, payload, {
    optimist: true,
    promise: proposalsQuery(
      {
        productId,
        arrivalDate,
        duration,
        departureCity,
        numberAttendees: attendees.adults + attendees.kids.length,
        adults: new Array(attendees.adults).fill({}),
        // $FlowFixMe
        kids: attendees.kids,
        fetchActivityCategories: isFeatureFlipped(appContext)('booking.displayActivities'),
        ...(isSeller(appContext)
          ? { salesNetworkId: location.queryParams.cxo_sales_network_id }
          : null),
      },
      appContext,
    ),
  });
}

function addTransport(
  transportId: string,
  proposalsId: Array<string>,
  isJoyview: boolean,
  appContext: AppContext,
  globalLoaderLabel: string,
  toastLabel: string,
) {
  return (dispatch: ReduxDispatch): * => {
    return dispatch(
      createOptimisticAction(
        ADD_TRANSPORT,
        { globalLoaderLabel, toastLabel },
        {
          optimist: true,
          promise: addTransportMutation(transportId, proposalsId, isJoyview, appContext),
        },
      ),
    );
  };
}

function displayToastOnContextShare(toastLabel: string) {
  return { type: DISPLAY_TOAST_ON_CONTEXT_SHARE, payload: { toastLabel } };
}

export type AddAccommodationArgs = {
  arrangement: AccommodationArrangement,
  proposalsId: Array<string>,
  isJoyview: boolean,
  appContext: AppContext,
  globalLoaderLabel: string,
  toastLabel: string,
};
function addAccommodation({
  arrangement,
  proposalsId,
  isJoyview,
  appContext,
  globalLoaderLabel,
  toastLabel,
}: AddAccommodationArgs) {
  return (dispatch: ReduxDispatch): * => {
    return dispatch(
      createOptimisticAction(
        ADD_ACCOMMODATION,
        { globalLoaderLabel, toastLabel },
        {
          optimist: true,
          promise: addAccommodationMutation(arrangement, proposalsId, isJoyview, appContext),
        },
      ),
    );
  };
}

function updateAttendees({ proposalId, attendees, appContext }: updateAttendeesMutationType) {
  return (dispatch: ReduxDispatch): * => {
    return updateAttendeesMutation({
      proposalId,
      attendees,
      appContext,
    }).then(data => {
      // $FlowFixMe it can be modifyAttendeesSellerMultipleFamilies or modifyAttendees
      const payload: {} = Object.values(data)[0];
      const { errors } = payload;
      if (errors) {
        // We don't do anything specific with the errors, but this will let us display per field errors
        logger.error(errors[0]);
        throw new Error('Unhandled field form error');
      }
      const neolaneId = get(payload, 'proposal.attendees.list[0].customerId');
      const cookieToSet = {
        ...trackingStorageApi.get(BE_TRACKING),
        neolaneId,
        BE_customerStatus: payload.customerStatus,
      };
      trackingStorageApi.set(cookieToSet);
      dispatch(createAction(UPDATE_ATTENDEES, payload));
    });
  };
}

function updateInsurances(
  proposalId: string,
  insuranceId: string,
  attendees: Array<LightAttendee>,
  app: AppContext,
) {
  return (dispatch: ReduxDispatch): * => {
    return updateInsurancesMutation(proposalId, insuranceId, attendees, app).then(payload => {
      dispatch(createAction(UPDATE_INSURANCES, payload));
    });
  };
}

function deleteInsurances(proposalId: string, insuranceId: string, app: AppContext) {
  return (dispatch: ReduxDispatch): * => {
    return deleteInsurancesMutation(proposalId, insuranceId, app).then(payload => {
      dispatch(createAction(DELETE_INSURANCES, payload));
    });
  };
}

function clearProposals() {
  return createAction(CLEAR_PROPOSALS);
}

const fetchSelectedProposalPayment = (
  selectedProposal: $ReadOnly<Proposal>,
  appContext: AppContext,
) => {
  return createOptimisticAction(
    FETCH_SELECTED_PROPOSAL_PAYMENT,
    { selectedProposalId: selectedProposal.id },
    {
      optimist: true,
      promise: fetchPayment(selectedProposal.id, appContext),
    },
  );
};

const saveCustomerProposalsHandler = (
  proposalId: string,
  appContext: AppContext,
  customerId: string,
) => {
  return (dispatch: ReduxDispatch): * =>
    dispatch(
      createOptimisticAction(
        SAVE_CUSTOMER_PROPOSAL,
        {},
        {
          optimist: true,
          promise: saveCustomerProposal(proposalId, appContext, customerId),
        },
      ),
    );
};

const addOrReplaceAdditionalServices = ({
  proposalIds,
  services,
  showNotification = false,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  services: $ReadOnlyArray<ServicesMutationType> | ServicesMutationType,
  showNotification?: boolean,
  appContext: AppContext,
}) => {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const proposal = getCurrentProposal(getState());
    const servicesIds = Array.isArray(services) ? services.map(({ id }) => id) : services.id;
    const additionalServicesValues = Object.values(proposal.additionalServices);
    const isUpdatingAlreadyReservedService = additionalServicesValues.some(oldServices =>
      // $FlowFixMe
      oldServices.some(
        ({ pricingInfo }) => servicesIds.includes(pricingInfo.id) && pricingInfo.isPartiallyRented,
      ),
    );
    const newReservedServiceToastLabel = additionalServicesValues.some(oldServices =>
      // $FlowFixMe
      oldServices.some(
        ({ pricingInfo }) => servicesIds.includes(pricingInfo.id) && pricingInfo.type === 'RENTAL',
      ),
    )
      ? 'booking.activities.skiEquipment.newEquipmentSelection'
      : 'booking.activities.newActivitySelection';

    return dispatch(
      createOptimisticAction(
        UPDATE_SERVICES,
        showNotification
          ? {
              toastLabel: isUpdatingAlreadyReservedService
                ? 'booking.activities.activityUpdate'
                : newReservedServiceToastLabel,
            }
          : {},
        {
          optimist: true,
          promise: putServicesMutation(proposalIds, services, appContext),
        },
      ),
    );
  };
};

const deleteAdditionalServices = ({
  proposalIds,
  serviceIds,
  attendeeIds,
  showNotification = false,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceIds: $ReadOnlyArray<string>,
  attendeeIds: $ReadOnlyArray<string> | string,
  showNotification?: boolean,
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType,
  appContext: AppContext,
}) => {
  return (dispatch: ReduxDispatch) =>
    dispatch(
      createOptimisticAction(
        DELETE_SERVICE,
        showNotification ? { toastLabel: 'booking.activities.activityUpdate' } : {},
        {
          optimist: true,
          promise: deleteServicesMutation({
            proposalIds,
            serviceIds,
            attendeeIds,
            overloadForSpecificProposalIds,
            appContext,
          }),
        },
      ),
    );
};

const addAdditionalService = ({
  proposalIds,
  serviceId,
  attendeeIds,
  schedules,
  showNotification = false,
  overloadForSpecificProposalIds,
  appContext,
}: {
  proposalIds: $ReadOnlyArray<string>,
  serviceId: string,
  attendeeIds: $ReadOnlyArray<string> | string,
  schedules: $ReadOnlyArray<{ startDate: string, endDate: string }>,
  showNotification?: boolean,
  overloadForSpecificProposalIds?: OverloadForSpecificProposalIdsType,
  appContext: AppContext,
}) => {
  return (dispatch: ReduxDispatch) =>
    dispatch(
      createOptimisticAction(
        ADD_SERVICE,
        showNotification ? { toastLabel: 'booking.activities.activityUpdate' } : {},
        {
          optimist: true,
          promise: addServiceMutation({
            proposalIds,
            serviceId,
            attendeeIds,
            schedules,
            overloadForSpecificProposalIds,
            appContext,
          }),
        },
      ),
    );
};

const prepareBe = (payload: *) => createAction(PREPARE_BE, payload);

const saveDonationToProposals = (donation: FormattedPrice) => (dispatch: ReduxDispatch) => {
  dispatch({ type: SAVE_DONATION_TO_PROPOSALS, payload: donation });
};

const switchProposalsOffers = (offers: Array<string>, appContext: AppContext) => (
  dispatch: ReduxDispatch,
  getState: GetReduxState,
) => {
  const state = getState();
  const proposals = proposalsSelector(state);
  // TODO : remove filter when proposals array in store is fixed and does not include a random object
  const proposalsIds = proposals.map(({ id }) => id).filter(id => !!id);
  return dispatch(
    createOptimisticAction(
      SWITCH_PROPOSALS_OFFERS,
      {},
      {
        optimist: true,
        promise: switchProposalsOffersMutation(proposalsIds, offers, appContext),
      },
    ),
  );
};

export {
  setPromoCode,
  addAccommodation,
  addTransport,
  addAdditionalService,
  deleteAdditionalServices,
  updateAttendees,
  clearProposals,
  fetchProposalsAndRedirect,
  fetchProposalsById,
  fetchSelectedProposalPayment,
  saveCustomerProposalsHandler,
  updateInsurances,
  deleteInsurances,
  prepareBe,
  displayToastOnContextShare,
  editCriteriaFetchProposals,
  simpleFetchProposals,
  saveDonationToProposals,
  addOrReplaceAdditionalServices,
  switchProposalsOffers,
};
